import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { celebrateBadge, celebrateMilestone, fireConfetti } from '../utils/celebrations';

const GamificationContext = createContext();

// Badge definitions
const BADGES = {
  first_step: {
    id: 'first_step',
    name: { fr: 'Premier Pas', en: 'First Step' },
    description: { fr: 'Commencez votre CV', en: 'Start your CV' },
    icon: '🚀',
    condition: (stats) => stats.sectionsCompleted >= 1,
    points: 10
  },
  halfway: {
    id: 'halfway',
    name: { fr: 'Mi-Chemin', en: 'Halfway There' },
    description: { fr: 'Atteignez 50% de complétion', en: 'Reach 50% completion' },
    icon: '🎯',
    condition: (stats) => stats.completion >= 50,
    points: 25
  },
  almost_there: {
    id: 'almost_there',
    name: { fr: 'Presque Là', en: 'Almost There' },
    description: { fr: 'Atteignez 75% de complétion', en: 'Reach 75% completion' },
    icon: '⭐',
    condition: (stats) => stats.completion >= 75,
    points: 50
  },
  cv_master: {
    id: 'cv_master',
    name: { fr: 'Maître du CV', en: 'CV Master' },
    description: { fr: 'Complétez votre CV à 100%', en: 'Complete your CV 100%' },
    icon: '🏆',
    condition: (stats) => stats.completion >= 100,
    points: 100
  },
  detail_oriented: {
    id: 'detail_oriented',
    name: { fr: 'Détaillé', en: 'Detailed' },
    description: { fr: 'Ajoutez 3+ expériences', en: 'Add 3+ experiences' },
    icon: '🔍',
    condition: (stats) => stats.experienceCount >= 3,
    points: 30
  },
  polyglot: {
    id: 'polyglot',
    name: { fr: 'Polyglotte', en: 'Polyglot' },
    description: { fr: 'Ajoutez 2+ langues', en: 'Add 2+ languages' },
    icon: '🌍',
    condition: (stats) => stats.languageCount >= 2,
    points: 20
  },
  skilled: {
    id: 'skilled',
    name: { fr: 'Expert', en: 'Expert' },
    description: { fr: 'Ajoutez 5+ compétences', en: 'Add 5+ skills' },
    icon: '💪',
    condition: (stats) => stats.skillCount >= 5,
    points: 25
  },
  certified: {
    id: 'certified',
    name: { fr: 'Certifié', en: 'Certified' },
    description: { fr: 'Ajoutez une certification', en: 'Add a certification' },
    icon: '📜',
    condition: (stats) => stats.certificationCount >= 1,
    points: 20
  },
  streak_master: {
    id: 'streak_master',
    name: { fr: 'En Série', en: 'On Fire' },
    description: { fr: 'Complétez 3 sections d\'affilée', en: 'Complete 3 sections in a row' },
    icon: '🔥',
    condition: (stats) => stats.streak >= 3,
    points: 40
  },
  personality_pro: {
    id: 'personality_pro',
    name: { fr: 'Perso Pro', en: 'Perso Pro' },
    description: { fr: 'Complétez le test de personnalité', en: 'Complete the personality test' },
    icon: '🧠',
    condition: (stats) => stats.personalityCompleted,
    points: 50
  }
};

// Level definitions
const LEVELS = [
  { name: { fr: 'Bronze', en: 'Bronze' }, minPoints: 0, color: '#CD7F32', icon: '🥉' },
  { name: { fr: 'Argent', en: 'Silver' }, minPoints: 50, color: '#C0C0C0', icon: '🥈' },
  { name: { fr: 'Or', en: 'Gold' }, minPoints: 150, color: '#FFD700', icon: '🥇' },
  { name: { fr: 'Platine', en: 'Platinum' }, minPoints: 300, color: '#E5E4E2', icon: '💎' },
  { name: { fr: 'Diamant', en: 'Diamond' }, minPoints: 500, color: '#B9F2FF', icon: '👑' }
];

export function GamificationProvider({ children }) {
  const [points, setPoints] = useState(() => {
    const saved = localStorage.getItem('gamification_points');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [unlockedBadges, setUnlockedBadges] = useState(() => {
    const saved = localStorage.getItem('gamification_badges');
    return saved ? JSON.parse(saved) : [];
  });

  const [streak, setStreak] = useState(() => {
    const saved = localStorage.getItem('gamification_streak');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [lastAction, setLastAction] = useState(null);
  const [showBadgeNotification, setShowBadgeNotification] = useState(null);
  const [showPointsAnimation, setShowPointsAnimation] = useState(null);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('gamification_points', points.toString());
  }, [points]);

  useEffect(() => {
    localStorage.setItem('gamification_badges', JSON.stringify(unlockedBadges));
  }, [unlockedBadges]);

  useEffect(() => {
    localStorage.setItem('gamification_streak', streak.toString());
  }, [streak]);

  // Get current level
  const getLevel = useCallback(() => {
    for (let i = LEVELS.length - 1; i >= 0; i--) {
      if (points >= LEVELS[i].minPoints) {
        return { ...LEVELS[i], index: i };
      }
    }
    return { ...LEVELS[0], index: 0 };
  }, [points]);

  // Get progress to next level
  const getLevelProgress = useCallback(() => {
    const currentLevel = getLevel();
    const nextLevelIndex = currentLevel.index + 1;
    
    if (nextLevelIndex >= LEVELS.length) {
      return { progress: 100, pointsToNext: 0, nextLevel: null };
    }

    const nextLevel = LEVELS[nextLevelIndex];
    const pointsInCurrentLevel = points - currentLevel.minPoints;
    const pointsNeededForNext = nextLevel.minPoints - currentLevel.minPoints;
    const progress = Math.min(100, (pointsInCurrentLevel / pointsNeededForNext) * 100);
    
    return {
      progress,
      pointsToNext: nextLevel.minPoints - points,
      nextLevel
    };
  }, [points, getLevel]);

  // Add points with animation
  const addPoints = useCallback((amount, reason = '') => {
    setPoints(prev => prev + amount);
    setShowPointsAnimation({ amount, reason });
    setTimeout(() => setShowPointsAnimation(null), 2000);
    
    // Check for level up
    const oldLevel = getLevel();
    setTimeout(() => {
      const newLevel = getLevel();
      if (newLevel.index > oldLevel.index) {
        celebrateMilestone(100); // Big celebration for level up
      }
    }, 100);
  }, [getLevel]);

  // Check and unlock badges
  const checkBadges = useCallback((stats) => {
    Object.values(BADGES).forEach(badge => {
      if (!unlockedBadges.includes(badge.id) && badge.condition(stats)) {
        // Unlock the badge
        setUnlockedBadges(prev => [...prev, badge.id]);
        addPoints(badge.points, badge.name.fr);
        
        // Show notification and celebrate
        setShowBadgeNotification(badge);
        celebrateBadge();
        
        // Hide notification after 4 seconds
        setTimeout(() => setShowBadgeNotification(null), 4000);
      }
    });
  }, [unlockedBadges, addPoints]);

  // Increment streak
  const incrementStreak = useCallback(() => {
    setStreak(prev => {
      const newStreak = prev + 1;
      if (newStreak === 3) {
        fireConfetti();
      }
      return newStreak;
    });
  }, []);

  // Reset streak
  const resetStreak = useCallback(() => {
    setStreak(0);
  }, []);

  // Record action
  const recordAction = useCallback((actionType, data = {}) => {
    setLastAction({ type: actionType, data, timestamp: Date.now() });
    
    // Award points for various actions
    const pointsMap = {
      'section_complete': 15,
      'field_filled': 2,
      'experience_added': 10,
      'education_added': 10,
      'skill_added': 3,
      'certification_added': 8,
      'language_added': 5,
      'chat_message': 1,
      'cv_downloaded': 20
    };

    if (pointsMap[actionType]) {
      addPoints(pointsMap[actionType], actionType);
    }
  }, [addPoints]);

  // Get comparison stats - encouraging message based on starting
  const getComparisonStats = useCallback(() => {
    // Encouraging message for starting - most people never begin
    if (points === 0) {
      return {
        percentile: 0,
        isStarter: true,
        message: {
          fr: "Bravo ! Vous êtes déjà en avance sur ceux qui n'osent pas commencer !",
          en: "Great! You're already ahead of those who never start!"
        }
      };
    }
    
    // Progress-based encouragement
    if (points < 50) {
      return {
        percentile: 25,
        isStarter: false,
        message: {
          fr: "Bon début ! Continuez pour débloquer plus de récompenses.",
          en: "Good start! Keep going to unlock more rewards."
        }
      };
    }
    
    // More points = better percentile
    const percentile = Math.min(95, 30 + Math.floor((points / 500) * 65));
    return {
      percentile,
      isStarter: false,
      message: {
        fr: `Impressionnant ! Votre CV surpasse ${percentile}% des utilisateurs.`,
        en: `Impressive! Your CV outperforms ${percentile}% of users.`
      }
    };
  }, [points]);

  // Reset all gamification data
  const resetGamification = useCallback(() => {
    setPoints(0);
    setUnlockedBadges([]);
    setStreak(0);
    localStorage.removeItem('gamification_points');
    localStorage.removeItem('gamification_badges');
    localStorage.removeItem('gamification_streak');
  }, []);

  const value = {
    points,
    unlockedBadges,
    streak,
    lastAction,
    showBadgeNotification,
    showPointsAnimation,
    badges: BADGES,
    levels: LEVELS,
    getLevel,
    getLevelProgress,
    addPoints,
    checkBadges,
    incrementStreak,
    resetStreak,
    recordAction,
    getComparisonStats,
    resetGamification,
    setShowBadgeNotification
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
}

export function useGamification() {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
}

export { BADGES, LEVELS };
